import React from 'react';
import type { ElectricalWire } from '../../model/types';
import type { Vec2 } from '@/core/types';

export interface WireRendererProps {
  wires: readonly ElectricalWire[];
  terminalPositions: Record<string, Vec2>; // "deviceId:terminalId" -> { x, y } (Pano uzayı koordinatları)
  wireEnergized?: Record<string, boolean>;
  selectedWireId?: string | null;
  onSelectWire?: (wireId: string) => void;
  // Geçici olarak sürüklenen kablo (Kablo çekme anı)
  draggedWire?: {
    from: Vec2;
    to: Vec2;
    color: string;
  } | null;
}

/**
 * Pano İçi Esnek Kablo Çizici (WireRenderer)
 * İki terminal vidası arasına gerçekçi yerçekimi eğrisi (Cubic Bezier Sagging Curve)
 * ve kablo derinlik gölgesi çizer. Enerjili kablolarda canlı akım ışıması uygular.
 */
export const WireRenderer: React.FC<WireRendererProps> = ({
  wires,
  terminalPositions,
  wireEnergized = {},
  selectedWireId = null,
  onSelectWire,
  draggedWire = null,
}) => {
  const getWirePath = (p1: Vec2, p2: Vec2, waypoints?: readonly Vec2[]): string => {
    if (waypoints && waypoints.length > 0) {
      let d = `M ${p1.x} ${p1.y}`;
      for (const pt of waypoints) {
        d += ` L ${pt.x} ${pt.y}`;
      }
      d += ` L ${p2.x} ${p2.y}`;
      return d;
    }

    // Doğal Yerçekimi Esnekliği (Natural Sagging Cubic Bezier)
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const dist = Math.hypot(dx, dy);

    // Klemens yönüne ve mesafeye göre doğal kıvrım derinliği
    const sag = Math.min(60, Math.max(15, dist * 0.18));
    const cy1 = p1.y + (p1.y < p2.y ? sag : -sag * 0.5);
    const cy2 = p2.y + (p2.y < p1.y ? sag : -sag * 0.5);

    return `M ${p1.x} ${p1.y} C ${p1.x + dx * 0.25} ${cy1}, ${p1.x + dx * 0.75} ${cy2}, ${p2.x} ${p2.y}`;
  };

  return (
    <g id="workshop-wires-layer" style={{ pointerEvents: 'auto' }}>
      <defs>
        {/* Kablo Zemin Gölgesi */}
        <filter id="wireShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="1" dy="3" stdDeviation="2.5" floodColor="#090d14" floodOpacity="0.45" />
        </filter>
      </defs>

      {/* Sabit Bağlantılı Kablolar */}
      {wires.map((wire) => {
        const fromKey = `${wire.from.deviceId}:${wire.from.terminalId}`;
        const toKey = `${wire.to.deviceId}:${wire.to.terminalId}`;
        const p1 = terminalPositions[fromKey];
        const p2 = terminalPositions[toKey];

        if (!p1 || !p2) return null;

        const path = getWirePath(p1, p2, wire.panelWaypoints);
        const isSelected = selectedWireId === wire.id;
        const isEnergized = wireEnergized[wire.id] ?? false;
        const strokeWidth = wire.sectionMm2 >= 2.5 ? 3.8 : 2.6;

        return (
          <g
            key={wire.id}
            style={{ cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onSelectWire?.(wire.id);
            }}
          >
            {/* 1. Derinlik Düşme Gölgesi */}
            <path
              d={path}
              fill="none"
              stroke="#090d14"
              strokeWidth={strokeWidth + 2}
              strokeOpacity="0.4"
              filter="url(#wireShadow)"
            />

            {/* 2. Enerjili Hat Canlı Parlama Halesi */}
            {isEnergized && (
              <path
                d={path}
                fill="none"
                stroke={wire.color === '#0284c7' ? '#38bdf8' : '#f59e0b'}
                strokeWidth={strokeWidth + 3.5}
                strokeOpacity="0.5"
                strokeLinecap="round"
              />
            )}

            {/* 3. Ana İzolasyon Gövdesi */}
            <path
              d={path}
              fill="none"
              stroke={wire.color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />

            {/* Topraklama (Sarı-Yeşil) Çift Renk Şerit Efekti */}
            {wire.color === '#84cc16' && (
              <path
                d={path}
                fill="none"
                stroke="#eab308"
                strokeWidth={strokeWidth * 0.5}
                strokeDasharray="8,8"
                strokeLinecap="round"
              />
            )}

            {/* 4. Seçim Vurgusu */}
            {isSelected && (
              <path
                d={path}
                fill="none"
                stroke="#38bdf8"
                strokeWidth={strokeWidth + 4}
                strokeDasharray="4,4"
                strokeOpacity="0.8"
              />
            )}
          </g>
        );
      })}

      {/* Sürüklenmekte Olan Geçici Kablo (Canlı Bağlantı Çekme) */}
      {draggedWire && (
        <g style={{ pointerEvents: 'none' }}>
          <path
            d={getWirePath(draggedWire.from, draggedWire.to)}
            fill="none"
            stroke={draggedWire.color}
            strokeWidth="3.2"
            strokeDasharray="4,3"
            strokeLinecap="round"
          />
          <circle cx={draggedWire.to.x} cy={draggedWire.to.y} r="5" fill={draggedWire.color} stroke="#ffffff" strokeWidth="1.5" />
        </g>
      )}
    </g>
  );
};
